import { Page, TextField, Modal } from '../components/forms';
import { useEffect, useRef, useState } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Checkbox,
  Divider,
  Banner,
  Badge,
  Spinner,
  ResourceList,
  ResourceItem,
  Avatar,
  Icon,
  Box,
  Collapsible,
} from '@shopify/polaris';
import { PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  LockFilledIcon,
} from '@shopify/polaris-icons';
import { useApi } from '../hooks/useApi';

interface Permission {
  id: number;
  code: string;
  name: string;
  description: string;
  category: string;
  is_dangerous: boolean;
}

interface Role {
  id: number;
  shop_id: string | null;
  name: string;
  description: string;
  dynamic_summary?: string;
  is_system: boolean;
  is_default: boolean;
  color: string;
  permission_ids: number[];
  permission_count: number;
  user_count?: number;
}

interface User {
  id: string;
  email: string;
  name: string;
  role_ids: number[];
  organization_role?: string | null;
  roles: Array<{ id: number; name: string; color: string }>;
}

export default function RoleBuilder() {
  const { fetchApi, error } = useApi();
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formColor, setFormColor] = useState('#6b7280');
  const [formPermissions, setFormPermissions] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [loadingRole, setLoadingRole] = useState(false);
  const [roleLoadFailed, setRoleLoadFailed] = useState(false);
  const roleRequest = useRef(0);
  const [successMessage, setSuccessMessage] = useState('');

  // Category collapse state
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());


  // User role editing state
  const [showUserRoleModal, setShowUserRoleModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedUserRoles, setSelectedUserRoles] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    const [rolesData, permissionsData, usersData] = await Promise.all([
      fetchApi<{ roles: Role[] }>('/rbac/roles'),
      fetchApi<{ permissions: Permission[] }>('/rbac/permissions'),
      fetchApi<{ users: User[] }>('/rbac/users'),
    ]);

    if (rolesData?.roles) setRoles(rolesData.roles);
    if (permissionsData?.permissions) setPermissions(permissionsData.permissions);
    if (usersData?.users) setUsers(usersData.users);
    setLoading(false);
  }

  // Permission lookup map for fast access
  const permissionMap = permissions.reduce((acc, perm) => {
    acc[perm.code] = perm;
    return acc;
  }, {} as Record<string, Permission>);

  // Navigation-based permission structure matching the sidebar menu
  // Each section contains menu items with their required permissions
  const navigationPermissions = [
    {
      section: 'Dashboard',
      icon: '📊',
      items: [
        { label: 'Dashboard', permissions: ['dashboard.stats.view'] },
      ],
    },
    {
      section: 'Calendar',
      icon: '📅',
      items: [
        { label: 'Shipping Calendar', permissions: ['calendar.view', 'orders.calendar.view'] },
        { label: 'Delivery Rules', permissions: ['rules.list', 'rules.view', 'rules.create', 'rules.update', 'rules.delete'] },
        { label: 'Blocked Dates', permissions: ['rules.blocked_dates.manage'] },
      ],
    },
    {
      section: 'Configuration',
      icon: '⚙️',
      items: [
        { label: 'Customise Storefront', permissions: ['settings.storefront.view', 'settings.storefront.update'] },
        { label: 'Delivery Zones', permissions: ['zones.list', 'zones.view', 'zones.create', 'zones.update', 'zones.delete'] },
        { label: 'Delivery Capacity', permissions: ['delivery.view', 'delivery.manage'] },
        { label: 'Weight Rules', permissions: ['delivery.view', 'delivery.manage'] },
        { label: 'Shipping Settings', permissions: ['shipping.zones.view', 'shipping.zones.manage', 'shipping.surcharges.manage'] },
        { label: 'Product Settings', permissions: ['products.view', 'products.update', 'products.rules.manage'] },
      ],
    },
    {
      section: 'Orders',
      icon: '📦',
      items: [
        { label: 'All Orders', permissions: ['orders.list', 'orders.view', 'orders.edit', 'orders.status.update', 'orders.delivery_date.update', 'orders.delivery_status.update', 'orders.allocate', 'orders.unallocate', 'orders.reallocate', 'orders.update'] },
        { label: 'Merge Rules', permissions: ['orders.view'] },
        { label: 'Automation', permissions: ['settings.view', 'settings.manage'] },
      ],
    },
    {
      section: 'Delivery Routes',
      icon: '🚚',
      items: [
        { label: 'Routes', permissions: ['routes.list', 'routes.view', 'routes.update', 'routes.delete', 'routes.optimize', 'routes.operate'] },
        { label: 'Build Route', permissions: ['routes.create'] },
      ],
    },
    {
      section: 'Customer Returns',
      icon: '↩️',
      items: [
        { label: 'Customer Returns', permissions: ['returns.list', 'returns.view', 'returns.approve', 'returns.reject', 'returns.receive', 'returns.process', 'returns.restock'] },
        { label: 'Return Reasons', permissions: ['returns.list', 'returns.manage'] },
      ],
    },
    {
      section: 'Supplier Returns',
      icon: '📤',
      items: [
        { label: 'Supplier Returns', permissions: ['supplier_returns.list', 'supplier_returns.view', 'supplier_returns.create', 'supplier_returns.update', 'supplier_returns.delete', 'supplier_returns.process', 'supplier_returns.manage'] },
        { label: 'Supplier Invoices', permissions: ['supplier_invoices.list', 'supplier_invoices.view', 'supplier_invoices.create', 'supplier_invoices.update', 'supplier_invoices.delete'] },
      ],
    },
    {
      section: 'Purchase Orders',
      icon: '🛒',
      items: [
        { label: 'Purchase Orders', permissions: ['inventory.view', 'inventory.manage'] },
        { label: 'Suppliers', permissions: ['inventory.view', 'inventory.manage'] },
      ],
    },
    {
      section: 'Warehouse',
      icon: '🏭',
      items: [
        { label: 'Products', permissions: ['products.list', 'products.view', 'products.update', 'products.discontinue'] },
        { label: 'Vendor Rules', permissions: ['products.vendors.list', 'products.vendors.manage'] },
        { label: 'Alternative Products', permissions: ['warehouse.alternatives.list', 'warehouse.alternatives.manage'] },
        { label: 'Warehouses', permissions: ['warehouses.list', 'warehouses.view', 'warehouses.create', 'warehouses.sync', 'warehouses.locations.list', 'warehouses.locations.create', 'warehouses.locations.import'] },
        { label: 'Inventory', permissions: ['inventory.list', 'inventory.view', 'inventory.edit', 'inventory.manage', 'inventory.receive', 'inventory.transfer', 'inventory.adjust', 'inventory.dispatch', 'inventory.movements.view', 'inventory.sync'] },
        { label: 'Pick Lists', permissions: ['picking.lists.list', 'picking.lists.view', 'picking.lists.create', 'picking.lists.pick', 'picking.lists.complete', 'picking.dispatch', 'picking.signature', 'picking.manifest.view'] },
        { label: 'Packing Lists', permissions: ['warehouse.packing_lists.list', 'warehouse.packing_lists.create', 'warehouse.packing_lists.read', 'warehouse.packing_lists.update', 'warehouse.packing_lists.delete'] },
        { label: 'Stock Takes', permissions: ['stock_takes.list', 'stock_takes.view', 'stock_takes.create', 'stock_takes.count', 'stock_takes.complete'] },
        { label: 'Dynamic Slotting', permissions: ['inventory.view'] },
        { label: 'Put-Away Tasks', permissions: ['inventory.view'] },
        { label: 'Serial Numbers', permissions: ['inventory.view'] },
        { label: 'Damage Reports', permissions: ['inventory.view', 'inventory.edit'] },
        { label: 'Transfer Orders', permissions: ['inventory.view', 'inventory.transfer'] },
      ],
    },
    {
      section: 'Reports',
      icon: '📈',
      items: [
        { label: 'Reports Hub', permissions: ['reports.view'] },
        { label: 'Sales Velocity', permissions: ['reports.view'] },
        { label: 'Reorder Recommendations', permissions: ['reports.view'] },
        { label: 'Best Sellers', permissions: ['reports.view'] },
        { label: 'Slow Moving Stock', permissions: ['reports.view'] },
        { label: 'Picker Productivity', permissions: ['reports.view'] },
        { label: 'Backorder Report', permissions: ['reports.backorder'] },
        { label: 'Product Flow', permissions: ['reports.product_flow'] },
        { label: 'Intrastat (EU)', permissions: ['reports.view'] },
        { label: 'Stock Valuation', permissions: ['reports.view'] },
        { label: 'Profit Analysis', permissions: ['reports.view'] },
        { label: 'Delivery Costs', permissions: ['reports.view', 'reports.edit'] },
        { label: 'Error Analytics', permissions: ['reports.view'] },
        { label: 'International Docs', permissions: ['orders.view'] },
        { label: 'Export Reports', permissions: ['reports.export', 'reports.download'] },
      ],
    },
    {
      section: 'Settings',
      icon: '🔧',
      items: [
        { label: 'Data Import', permissions: ['sync.status.view', 'sync.orders', 'sync.products', 'sync.bulk'] },
        { label: 'Sync Settings', permissions: ['sync.status.view', 'sync.schedules.view', 'sync.schedules.manage'] },
        { label: 'Storage Fees', permissions: ['settings.view', 'settings.update'] },
        { label: 'Roles & Permissions', permissions: ['settings.roles'] },
        { label: 'General Settings', permissions: ['settings.view', 'settings.update', 'settings.edit'] },
        { label: 'Billing', permissions: ['settings.billing.view', 'settings.billing.manage'] },
        { label: 'Webhooks', permissions: ['settings.webhooks.view', 'settings.webhooks.manage'] },
      ],
    },
    {
      section: 'Help',
      icon: '❓',
      items: [
        { label: 'Help & Documentation', permissions: ['help.view'] },
      ],
    },
  ];

  // Helper to get permission ID by code
  const getPermissionId = (code: string): number | null => {
    const perm = permissionMap[code];
    return perm?.id || null;
  };

  // Check if menu item is enabled (has at least the view permission)
  const isMenuItemEnabled = (permCodes: string[]): boolean => {
    const viewPerm = permCodes[0]; // First permission is always the view permission
    const permId = getPermissionId(viewPerm);
    return permId ? formPermissions.has(permId) : false;
  };

  // Toggle all permissions for a menu item
  const toggleMenuItem = (permCodes: string[]) => {
    const permIds = permCodes.map(code => getPermissionId(code)).filter((id): id is number => id !== null);
    const allSelected = permIds.every(id => formPermissions.has(id));

    setFormPermissions(prev => {
      const next = new Set(prev);
      if (allSelected) {
        permIds.forEach(id => next.delete(id));
      } else {
        permIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  // Toggle all permissions for a section
  const toggleSection = (section: typeof navigationPermissions[0]) => {
    const allPermCodes = section.items.flatMap(item => item.permissions);
    const allPermIds = allPermCodes.map(code => getPermissionId(code)).filter((id): id is number => id !== null);
    const uniquePermIds = [...new Set(allPermIds)];
    const allSelected = uniquePermIds.every(id => formPermissions.has(id));

    setFormPermissions(prev => {
      const next = new Set(prev);
      if (allSelected) {
        uniquePermIds.forEach(id => next.delete(id));
      } else {
        uniquePermIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  // Count selected permissions in a section
  const getSectionStats = (section: typeof navigationPermissions[0]): { selected: number; total: number } => {
    const allPermCodes = section.items.flatMap(item => item.permissions);
    const uniquePermCodes = [...new Set(allPermCodes)];
    const permIds = uniquePermCodes.map(code => getPermissionId(code)).filter((id): id is number => id !== null);
    const selected = permIds.filter(id => formPermissions.has(id)).length;

    return { selected, total: permIds.length };
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const togglePermission = (permId: number) => {
    setFormPermissions(prev => {
      const next = new Set(prev);
      if (next.has(permId)) {
        next.delete(permId);
      } else {
        next.add(permId);
      }
      return next;
    });
  };

  const openCreateModal = () => {
    roleRequest.current++;
    setFormName('');
    setFormDescription('');
    setFormColor('#6b7280');
    setFormPermissions(new Set());
    setExpandedCategories(new Set());
    setShowCreateModal(true);
  };

  const openEditModal = async (role: Role) => {
    setSelectedRole(role);
    setFormName(role.name);
    setFormDescription(role.description);
    setFormColor(role.color);
    setFormPermissions(new Set()); // Start with empty while loading
    setExpandedCategories(new Set());
    setShowEditModal(true);
    setLoadingRole(true);
    setRoleLoadFailed(false);
    const request = ++roleRequest.current;

    // Fetch role permissions - API returns { success: true, role: { permissions: [{id, code, ...}, ...] } }
    const data = await fetchApi<{ success?: boolean; error?: string; role?: Role & { permissions?: Array<{ id: number }> } }>(`/rbac/roles/${role.id}`);

    if (request !== roleRequest.current) return;
    setLoadingRole(false);
    if (!data || data.success === false || !Array.isArray(data.role?.permissions)) {
      setRoleLoadFailed(true);
      return;
    }
    setFormPermissions(new Set(data.role.permissions.map(permission => permission.id)));
  };

  const openUsersModal = (role: Role) => {
    setSelectedRole(role);
    setShowUsersModal(true);
  };

  const handleCreate = async () => {
    setSaving(true);
    const result = await fetchApi<{ success: boolean; role: Role }>('/rbac/roles', {
      method: 'POST',
      body: JSON.stringify({
        name: formName,
        description: formDescription,
        color: formColor,
        permission_ids: Array.from(formPermissions),
      }),
    });

    setSaving(false);
    if (result?.success) {
      setShowCreateModal(false);
      setSuccessMessage('Role created successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      loadData();
    }
  };

  const handleUpdate = async () => {
    if (!selectedRole || selectedRole.is_system || loadingRole || roleLoadFailed) return;

    setSaving(true);
    const result = await fetchApi<{ success: boolean }>(`/rbac/roles/${selectedRole.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        name: formName,
        description: formDescription,
        color: formColor,
        permission_ids: Array.from(formPermissions),
      }),
    });

    setSaving(false);
    if (result?.success) {
      setShowEditModal(false);
      setSuccessMessage('Role updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      loadData();
    }
  };

  const handleDelete = async (role: Role) => {
    if (role.is_system) return;
    if (!confirm(`Are you sure you want to delete the role "${role.name}"?`)) return;

    const result = await fetchApi<{ success: boolean }>(`/rbac/roles/${role.id}`, {
      method: 'DELETE',
    });

    if (result?.success) {
      setSuccessMessage('Role deleted successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      loadData();
    }
  };

  const handleAssignUser = async (userId: string) => {
    if (!selectedRole) return;

    const result = await fetchApi<{ success: boolean }>(`/rbac/users/${userId}/roles/${selectedRole.id}`, {
      method: 'POST',
    });

    if (result?.success) {
      loadData();
    }
  };

  const handleUnassignUser = async (userId: string, roleId: number) => {
    const result = await fetchApi<{ success: boolean }>(`/rbac/users/${userId}/roles/${roleId}`, {
      method: 'DELETE',
    });

    if (result?.success) {
      loadData();
    }
  };

  // User role editing functions
  const openUserRoleModal = (user: User) => {
    setSelectedUser(user);
    setSelectedUserRoles(new Set(user.role_ids));
    setShowUserRoleModal(true);
  };

  const toggleUserRole = (roleId: number) => {
    setSelectedUserRoles(prev => {
      const next = new Set(prev);
      if (next.has(roleId)) {
        next.delete(roleId);
      } else {
        next.add(roleId);
      }
      return next;
    });
  };

  const handleSaveUserRoles = async () => {
    if (!selectedUser) return;

    setSaving(true);

    // Update user roles - set all selected roles
    const result = await fetchApi<{ success: boolean }>(`/rbac/users/${selectedUser.id}/roles`, {
      method: 'PUT',
      body: JSON.stringify({
        role_ids: Array.from(selectedUserRoles),
      }),
    });

    setSaving(false);
    if (result?.success) {
      setShowUserRoleModal(false);
      setSuccessMessage(`Roles updated for ${selectedUser.name || 'user'}`);
      setTimeout(() => setSuccessMessage(''), 3000);
      loadData();
    }
  };

  const renderPermissionsForm = () => {
    return (
      <BlockStack gap="400">
        {navigationPermissions.map(section => {
          const stats = getSectionStats(section);
          const allSelected = stats.selected === stats.total && stats.total > 0;
          const someSelected = stats.selected > 0 && stats.selected < stats.total;
          const isExpanded = expandedCategories.has(section.section);

          return (
            <Card key={section.section}>
              <BlockStack gap="300">
                {/* Section Header */}
                <InlineStack align="space-between" blockAlign="center">
                  <InlineStack gap="200" blockAlign="center">
                    <Checkbox
                      label=""
                      checked={allSelected || someSelected}
                      onChange={() => toggleSection(section)}
                    />
                    <Button
                      variant="plain"
                      onClick={() => toggleCategory(section.section)}
                      icon={isExpanded ? ChevronUpIcon : ChevronDownIcon}
                    >
                      {`${section.icon} ${section.section}`}
                    </Button>
                  </InlineStack>
                  <Badge tone={stats.selected > 0 ? 'success' : undefined}>
                    {`${stats.selected}/${stats.total}`}
                  </Badge>
                </InlineStack>

                {/* Section Items (Menu Items) */}
                <Collapsible open={isExpanded} id={`section-${section.section}`}>
                  <Box paddingInlineStart="400" paddingBlockStart="200">
                    <BlockStack gap="300">
                      {section.items.map((item, idx) => {
                        const itemEnabled = isMenuItemEnabled(item.permissions);
                        const itemPermIds = item.permissions
                          .map(code => getPermissionId(code))
                          .filter((id): id is number => id !== null);
                        const itemSelectedCount = itemPermIds.filter(id => formPermissions.has(id)).length;
                        const allItemPermsSelected = itemSelectedCount === itemPermIds.length;
                        const someItemPermsSelected = itemSelectedCount > 0 && itemSelectedCount < itemPermIds.length;
                        const showPermDetails = expandedCategories.has(`${section.section}-${idx}`);

                        // Check for dangerous permissions
                        const hasDangerousPerm = item.permissions.some(code => {
                          const perm = permissions.find(p => p.code === code);
                          return perm?.is_dangerous;
                        });

                        return (
                          <div
                            key={idx}
                            style={{
                              padding: '12px',
                              backgroundColor: itemEnabled ? '#f1f8ff' : '#fafafa',
                              borderRadius: '8px',
                              border: '1px solid #e1e3e5',
                            }}
                          >
                            <BlockStack gap="200">
                              {/* Menu Item Toggle */}
                              <InlineStack align="space-between" blockAlign="center">
                                <InlineStack gap="200" blockAlign="center">
                                  <Checkbox
                                    label=""
                                    checked={allItemPermsSelected || someItemPermsSelected}
                                    onChange={() => toggleMenuItem(item.permissions)}
                                  />
                                  <BlockStack gap="050">
                                    <InlineStack gap="100" blockAlign="center">
                                      <Text as="span" fontWeight="medium">{item.label}</Text>
                                      {hasDangerousPerm && (
                                        <Badge tone="critical" size="small">Sensitive</Badge>
                                      )}
                                    </InlineStack>
                                    <Text as="span" tone="subdued" variant="bodySm">
                                      {itemSelectedCount} of {itemPermIds.length} permissions
                                    </Text>
                                  </BlockStack>
                                </InlineStack>
                                {itemPermIds.length > 1 && (
                                  <Button
                                    variant="plain"
                                    size="slim"
                                    onClick={() => toggleCategory(`${section.section}-${idx}`)}
                                    icon={showPermDetails ? ChevronUpIcon : ChevronDownIcon}
                                  >
                                    Details
                                  </Button>
                                )}
                              </InlineStack>

                              {/* Individual Permissions (expandable) */}
                              {itemPermIds.length > 1 && (
                                <Collapsible open={showPermDetails} id={`item-${section.section}-${idx}`}>
                                  <Box paddingInlineStart="600" paddingBlockStart="200">
                                    <BlockStack gap="150">
                                      {item.permissions.map(code => {
                                        const perm = permissions.find(p => p.code === code);
                                        if (!perm) return null;
                                        return (
                                          <InlineStack key={code} gap="200" blockAlign="center">
                                            <Checkbox
                                              label={perm.name}
                                              checked={formPermissions.has(perm.id)}
                                              onChange={() => togglePermission(perm.id)}
                                            />
                                            {perm.is_dangerous && (
                                              <Badge tone="critical" size="small">Dangerous</Badge>
                                            )}
                                          </InlineStack>
                                        );
                                      })}
                                    </BlockStack>
                                  </Box>
                                </Collapsible>
                              )}
                            </BlockStack>
                          </div>
                        );
                      })}
                    </BlockStack>
                  </Box>
                </Collapsible>
              </BlockStack>
            </Card>
          );
        })}
      </BlockStack>
    );
  };

  if (loading) {
    return (
      <Page title="Role Builder">
        <Layout>
          <Layout.Section>
            <Card>
              <InlineStack align="center" blockAlign="center" gap="200">
                <Spinner size="small" />
                <Text as="span">Loading roles...</Text>
              </InlineStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Role Builder"
      subtitle="Manage roles and permissions for your team"
      primaryAction={{
        icon: PlusIcon, content: 'Create Role',
        onAction: openCreateModal,
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {successMessage && (
          <Layout.Section>
            <Banner tone="success">{successMessage}</Banner>
          </Layout.Section>
        )}

        {/* System Roles */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">System Roles</Text>
              <Text as="p" tone="subdued">
                These are built-in roles that cannot be modified. They serve as templates for common permission sets.
              </Text>
              <Divider />
              <ResourceList
                items={roles.filter(r => r.is_system)}
                renderItem={(role) => (
                  <ResourceItem
                    id={String(role.id)}
                    accessibilityLabel={role.name}
                    onClick={() => openEditModal(role)}
                    media={
                      <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: role.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <Icon source={LockFilledIcon} tone="base" />
                      </div>
                    }
                  >
                    <BlockStack gap="100">
                      <InlineStack gap="200" blockAlign="center">
                        <Text as="span" variant="bodyMd" fontWeight="bold">
                          {role.name}
                        </Text>
                        <Text as="span" tone="subdued">{role.user_count ?? 0} users</Text>
                        {role.is_default && (
                          <Badge tone="info">Default</Badge>
                        )}
                        <Badge>{`${role.permission_count} permissions`}</Badge>
                      </InlineStack>
                      <Text as="span" tone="subdued">{role.dynamic_summary || role.description}</Text>
                      <InlineStack gap="200">
                        <Button variant="plain" onClick={() => openEditModal(role)}>
                          View Permissions
                        </Button>
                        <Button variant="plain" onClick={() => openUsersModal(role)}>
                          View Users
                        </Button>
                      </InlineStack>
                    </BlockStack>
                  </ResourceItem>
                )}
              />
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Custom Roles */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Custom Roles</Text>
              <Text as="p" tone="subdued">
                Create custom roles with specific permission sets for your team.
              </Text>
              <Divider />
              {roles.filter(r => !r.is_system).length === 0 ? (
                <Box padding="400">
                  <BlockStack gap="200" inlineAlign="center">
                    <Text as="p" tone="subdued">No custom roles yet.</Text>
                    <Button icon={PlusIcon} onClick={openCreateModal}>Create your first role</Button>
                  </BlockStack>
                </Box>
              ) : (
                <ResourceList
                  items={roles.filter(r => !r.is_system)}
                  renderItem={(role) => (
                    <ResourceItem
                      id={String(role.id)}
                      accessibilityLabel={role.name}
                      onClick={() => openEditModal(role)}
                      media={
                        <div style={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          backgroundColor: role.color,
                        }} />
                      }
                      shortcutActions={[
                        {
                          content: 'Edit',
                          onAction: () => openEditModal(role),
                        },
                        {
                          content: 'Delete',
                          onAction: () => handleDelete(role),
                        },
                      ]}
                    >
                      <BlockStack gap="100">
                        <InlineStack gap="200" blockAlign="center">
                          <Text as="span" variant="bodyMd" fontWeight="bold">
                            {role.name}
                          </Text>
                          <Text as="span" tone="subdued">{role.user_count ?? 0} users</Text>
                          <Badge>{`${role.permission_count} permissions`}</Badge>
                        </InlineStack>
                        <Text as="span" tone="subdued">{role.dynamic_summary || role.description}</Text>
                        <InlineStack gap="200">
                          <Button variant="plain" onClick={() => openEditModal(role)}>
                            Edit Permissions
                          </Button>
                          <Button variant="plain" onClick={() => openUsersModal(role)}>
                            Manage Users
                          </Button>
                        </InlineStack>
                      </BlockStack>
                    </ResourceItem>
                  )}
                />
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Team Members */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">Team Members</Text>
                <Text as="p" tone="subdued">
                  Manage access for your warehouse team. Invite people through your company settings, then choose their roles here.
                </Text>
              </BlockStack>
              <Divider />
              {users.length === 0 ? (
                <Box padding="400">
                  <BlockStack gap="200" inlineAlign="center">
                    <Text as="p" tone="subdued">
                      No team members are connected to this warehouse account yet. Invite them through your company settings.
                    </Text>
                  </BlockStack>
                </Box>
              ) : (
                <ResourceList
                  items={users}
                  renderItem={(user) => (
                    <ResourceItem
                      id={user.id}
                      accessibilityLabel={user.name || user.email}
                      onClick={() => openUserRoleModal(user)}
                      media={<Avatar customer name={user.name || user.email} />}
                    >
                      <InlineStack align="space-between" blockAlign="center">
                        <BlockStack gap="100">
                          <Text as="span" variant="bodyMd" fontWeight="bold">
                            {user.name || 'Unknown'}
                          </Text>
                          <Text as="span" tone="subdued">{user.email || 'No email'}</Text>
                          <InlineStack gap="100">
                            {user.roles.length > 0 ? (
                              user.roles.map(role => (
                                <Badge key={role.id} tone="info">
                                  {role.name}
                                </Badge>
                              ))
                            ) : (
                              <Badge tone="warning">No role assigned</Badge>
                            )}
                          </InlineStack>
                        </BlockStack>
                        <Button onClick={() => openUserRoleModal(user)}>
                          Edit Roles
                        </Button>
                      </InlineStack>
                    </ResourceItem>
                  )}
                />
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Create Role Modal */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Role"
        primaryAction={{
          icon: PlusIcon, content: 'Create Role',
          onAction: handleCreate,
          loading: saving,
          disabled: !formName.trim(),
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setShowCreateModal(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Role Name"
              requiredIndicator value={formName}
              onChange={setFormName}
              autoComplete="off"
              placeholder="e.g., Warehouse Supervisor"
            />
            <TextField
              label="Description"
              value={formDescription}
              onChange={setFormDescription}
              autoComplete="off"
              multiline={2}
              placeholder="Describe what this role can do"
            />
            <InlineStack gap="200" blockAlign="end">
              <div style={{ flex: 1 }}>
                <TextField
                  label="Color"
                  value={formColor}
                  onChange={setFormColor}
                  autoComplete="off"
                  helpText="Enter a hex color code (e.g., #6b7280)"
                />
              </div>
              <div style={{
                width: 36,
                height: 36,
                borderRadius: 4,
                backgroundColor: formColor,
                border: '1px solid #ccc',
              }} />
            </InlineStack>
          </BlockStack>
        </Modal.Section>
        <Modal.Section>
          <BlockStack gap="400">
            <Text as="h3" variant="headingSm">Permissions</Text>
            <Text as="p" tone="subdued">
              Select the permissions this role should have. {formPermissions.size} selected.
            </Text>
            {renderPermissionsForm()}
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Edit Role Modal */}
      <Modal
        open={showEditModal}
        onClose={() => { roleRequest.current++; setShowEditModal(false); }}
        title={`${selectedRole?.is_system ? 'View Role' : 'Edit Role'}: ${selectedRole?.name}`}
        primaryAction={{
          icon: selectedRole?.is_system ? PlusIcon : undefined, content: selectedRole?.is_system ? 'Create Custom Copy' : 'Save Changes',
          onAction: selectedRole?.is_system ? () => {
            setFormName(`${selectedRole.name} copy`);
            setSelectedRole(null);
            setShowEditModal(false);
            setShowCreateModal(true);
          } : handleUpdate,
          loading: saving || loadingRole,
          disabled: !formName.trim() || loadingRole || roleLoadFailed,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => { roleRequest.current++; setShowEditModal(false); } },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Role Name"
              requiredIndicator value={formName}
              onChange={setFormName}
              autoComplete="off"
              disabled={selectedRole?.is_system}
            />
            <TextField
              label="Description"
              value={formDescription}
              onChange={setFormDescription}
              autoComplete="off"
              multiline={2}
              disabled={selectedRole?.is_system}
            />
            {!selectedRole?.is_system && (
              <InlineStack gap="200" blockAlign="end">
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Color"
                    value={formColor}
                    onChange={setFormColor}
                    autoComplete="off"
                  />
                </div>
                <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: 4,
                  backgroundColor: formColor,
                  border: '1px solid #ccc',
                }} />
              </InlineStack>
            )}
          </BlockStack>
        </Modal.Section>
        <Modal.Section>
          <BlockStack gap="400">
            <Text as="h3" variant="headingSm">Permissions</Text>
            {selectedRole?.is_system ? (
              <Banner tone="info">
                Built-in roles cannot be changed. Create a custom copy to choose your own permissions. {formPermissions.size} permissions currently assigned.
              </Banner>
            ) : (
              <Text as="p" tone="subdued">
                Select the permissions this role should have. {formPermissions.size} selected.
              </Text>
            )}
            {roleLoadFailed ? <Banner tone="critical">The role could not be loaded. Close this window and try again before making changes.</Banner>
              : loadingRole ? <Spinner accessibilityLabel="Loading role permissions" size="small" />
              : selectedRole?.is_system ? <BlockStack gap="200">{permissions.filter(permission => formPermissions.has(permission.id)).map(permission =>
                <Text key={permission.id} as="p">{permission.name}</Text>)}</BlockStack>
              : renderPermissionsForm()}
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Users Modal */}
      <Modal
        open={showUsersModal}
        onClose={() => setShowUsersModal(false)}
        title={`Users with "${selectedRole?.name}" role`}
        secondaryActions={[
          { content: 'Close', onAction: () => setShowUsersModal(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text as="p" tone="subdued">
              Manage which users have this role assigned.
            </Text>
            <Divider />
            {users.length === 0 ? (
              <Text as="p" tone="subdued">No users available.</Text>
            ) : (
              <ResourceList
                items={users}
                renderItem={(user) => {
                  const hasRole = user.role_ids.includes(selectedRole?.id || 0);
                  return (
                    <ResourceItem
                      id={user.id}
                      accessibilityLabel={user.name || user.email}
                      onClick={() => {}}
                      media={<Avatar customer name={user.name || user.email} />}
                    >
                      <InlineStack align="space-between" blockAlign="center">
                        <BlockStack gap="050">
                          <Text as="span" fontWeight="bold">{user.name || 'Unknown'}</Text>
                          <Text as="span" tone="subdued">{user.email}</Text>
                        </BlockStack>
                        {hasRole ? (
                          <Button
                            variant="secondary"
                            tone="critical"
                            onClick={() => handleUnassignUser(user.id, selectedRole?.id || 0)}
                          >
                            Remove
                          </Button>
                        ) : (
                          <Button
                            variant="primary"
                            onClick={() => handleAssignUser(user.id)}
                          >
                            Assign
                          </Button>
                        )}
                      </InlineStack>
                    </ResourceItem>
                  );
                }}
              />
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Edit User Roles Modal */}
      <Modal
        open={showUserRoleModal}
        onClose={() => setShowUserRoleModal(false)}
        title={`Edit Roles for ${selectedUser?.name || selectedUser?.email || 'User'}`}
        primaryAction={{
          content: 'Save Changes',
          onAction: handleSaveUserRoles,
          loading: saving,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setShowUserRoleModal(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {selectedUser && (
              <BlockStack gap="200">
                <InlineStack gap="200" blockAlign="center">
                  <Avatar customer name={selectedUser.name || selectedUser.email} />
                  <BlockStack gap="050">
                    <Text as="span" fontWeight="bold">{selectedUser.name || 'Unknown'}</Text>
                    <Text as="span" tone="subdued">{selectedUser.email || 'No email'}</Text>
                  </BlockStack>
                </InlineStack>
              </BlockStack>
            )}
            <Divider />
            <Text as="h3" variant="headingSm">Assigned Roles</Text>
            <Text as="p" tone="subdued">
              Choose the permissions this person needs for this warehouse account. Company owners and administrators keep their company access.
            </Text>
            <BlockStack gap="200">
              {roles.map(role => (
                <div
                  key={role.id}
                  style={{
                    padding: '12px',
                    border: '1px solid #e1e3e5',
                    borderRadius: '8px',
                    backgroundColor: selectedUserRoles.has(role.id) ? '#f1f8ff' : 'transparent',
                    cursor: 'pointer',
                  }}
                  onClick={() => toggleUserRole(role.id)}
                >
                  <InlineStack gap="300" blockAlign="center">
                    <div onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        label=""
                        checked={selectedUserRoles.has(role.id)}
                        onChange={() => toggleUserRole(role.id)}
                      />
                    </div>
                    <div style={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: role.color,
                    }} />
                    <BlockStack gap="050">
                      <InlineStack gap="100" blockAlign="center">
                        <Text as="span" fontWeight="semibold">{role.name}</Text>
                        {role.is_system && (
                          <Badge tone="info">System</Badge>
                        )}
                        {role.is_default && (
                          <Badge>Default</Badge>
                        )}
                      </InlineStack>
                      <Text as="span" tone="subdued" variant="bodySm">
                        {role.dynamic_summary || role.description || `${role.permission_count} permissions`}
                      </Text>
                    </BlockStack>
                  </InlineStack>
                </div>
              ))}
            </BlockStack>
            {selectedUserRoles.size === 0 && !['owner', 'admin'].includes(selectedUser?.organization_role || '') && (
              <Banner tone="warning">
                No roles selected. Saving will remove this person’s warehouse permissions.
              </Banner>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
